import React, { useEffect } from 'react';
import { getUserAbility, isAuthLoggedIn } from 'modules/auth/selectors';
import { useDispatch, useSelector } from 'react-redux';

import { NO_PERMISSION_MESSAGE } from 'utils/constants/messages';
/* eslint-disable react/prop-types */
/* eslint-disable react/jsx-props-no-spreading */
import { Route } from 'react-router-dom';
import ScreenLoading from 'views/ui/screen-loading';
import { isAllowed } from 'utils/casl/ability-utils';
import { logout } from 'modules/auth/actions';
import { messageError } from 'views/ui/toast-message';

/**
 * Gate for private URLs that need a permission on top of a session — the video
 * console and the library are admin only, for instance.
 *
 * A signed-in user whose role does not carry the permission is signed out and
 * left on this app's login page with an explanation, instead of on a screen they
 * cannot use. Landing here is ordinary rather than an attack: a bookmark kept
 * after a role change, or a link passed along by an admin colleague, both arrive
 * this way.
 *
 * The log out is flagged as user initiated, which is what keeps the URL out of
 * the login page's `return` param. Carrying it would send the user straight back
 * to the page they are not allowed to see the moment they signed in again.
 */
const AuthorizedRoute = ({ permissions, render, ...rest }) => {
  const dispatch = useDispatch();
  const isLoggedIn = useSelector(isAuthLoggedIn);
  const ability = useSelector(getUserAbility);

  const allowed = isAllowed(permissions, ability);
  // Only a live session can be given up. Once the store is cleared this turns
  // false on its own, so the log out is asked for once.
  const mustSignOut = isLoggedIn === true && !allowed;

  useEffect(() => {
    if (mustSignOut) {
      messageError(NO_PERMISSION_MESSAGE);
      dispatch(logout({ userInitiated: true }));
    }
  }, [dispatch, mustSignOut]);

  return (
    <Route
      {...rest}
      render={(routeProps) =>
        allowed ? render(routeProps) : <ScreenLoading />
      }
    />
  );
};

export default AuthorizedRoute;
